import { useState, useCallback } from 'react';
import { HELP_DESK_PRIORITIES, HELP_DESK_STATUSES } from '@ubeya/shared/constants/helpDesk';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useHelpDeskCategories from '@ubeya/shared/hooks/account/useHelpDeskCategories';
import useHelpDeskAdmins from '@ubeya/shared/hooks/account/useHelpDeskAdmins';
import useHelpDeskUpdateTicket from '@ubeya/shared/hooks/account/useHelpDeskUpdateTicket';
import { useHelpDeskAnalytics } from './helpDeskAnalytics';

/**
 * Shared hook for help desk ticket metadata actions:
 * - Fetches categories and admins for the ticket's branch
 * - Provides update mutation for category/priority/assignee changes
 * - Handles category change with confirmation state
 * - Provides priority and assignee change handlers
 *
 * @param {Object} ticket - The ticket object (needs id, branchId, categoryId, assignedAdminId, priority, status)
 * @param {string|number} accountId
 * @returns {Object}
 */
const useHelpDeskTicketActions = (ticket, accountId) => {
  const { addResponseError } = useToaster();
  const { trackHelpDeskTicketEvent } = useHelpDeskAnalytics();

  const ticketId = ticket?.id;
  const branchId = ticket?.branchId;
  const isClosed = ticket?.status === HELP_DESK_STATUSES.CLOSED;
  const isEditable = !isClosed;

  // Fetch categories for dropdown
  const { categories } = useHelpDeskCategories({ accountId, branchId });

  // Fetch admins for assignee dropdown based on current ticket category permissions
  const { admins } = useHelpDeskAdmins({ accountId, branchId, categoryId: ticket?.categoryId });

  // Update mutation for category/priority/assignee changes
  const { updateTicket, isLoading: updateLoading } = useHelpDeskUpdateTicket({ accountId, ticketId });

  // Wrap updateTicket to handle errors
  const safeUpdate = useCallback(
    (body, onSuccess) => {
      updateTicket(body, {
        onSuccess,
        onError: addResponseError
      });
    },
    [updateTicket, addResponseError]
  );

  const trackMetadataChange = useCallback(
    ({ eventName, assignedAdminId, newValue, previousValue }) => {
      trackHelpDeskTicketEvent({
        eventName,
        context: 'Help Desk ticket metadata',
        ticket,
        eventProperties: {
          assignedAdminId,
          newValue,
          previousValue
        }
      });
    },
    [ticket, trackHelpDeskTicketEvent]
  );

  // Category change with confirmation
  const [pendingCategory, setPendingCategory] = useState(null);

  const handleCategoryChange = useCallback(
    (newCategoryId) => {
      if (String(newCategoryId) === String(ticket?.categoryId)) return;
      setPendingCategory(newCategoryId);
    },
    [ticket?.categoryId]
  );

  const confirmCategoryChange = useCallback(() => {
    if (!pendingCategory) {
      setPendingCategory(null);
      return;
    }
    const nextCategory = categories.find((cat) => Number(cat.id) === Number(pendingCategory));
    const nextAssignedIds = Array.isArray(nextCategory?.assignedAdminIds)
      ? nextCategory.assignedAdminIds.map((id) => Number(id)).filter(Boolean)
      : [];
    const hasCategoryAssigneeRestrictions = nextAssignedIds.length > 0;

    const shouldClearAssignedAdmin =
      hasCategoryAssigneeRestrictions && ticket?.assignedAdminId && !nextAssignedIds.includes(ticket?.assignedAdminId);
    const nextAssignedAdminId = shouldClearAssignedAdmin ? null : ticket?.assignedAdminId || null;
    const body = shouldClearAssignedAdmin
      ? { categoryId: pendingCategory, assignedAdminId: null }
      : { categoryId: pendingCategory };

    safeUpdate(body, () =>
      trackMetadataChange({
        eventName: 'Help Desk - Admin Changed Category',
        assignedAdminId: nextAssignedAdminId,
        newValue: pendingCategory,
        previousValue: ticket?.categoryId || null
      })
    );
    setPendingCategory(null);
  }, [pendingCategory, safeUpdate, categories, ticket?.assignedAdminId, ticket?.categoryId, trackMetadataChange]);

  const cancelCategoryChange = useCallback(() => {
    setPendingCategory(null);
  }, []);

  // Priority change
  const handlePriorityChange = useCallback(
    (value) => {
      const previousPriority = ticket?.priority || HELP_DESK_PRIORITIES.NONE;
      if (value === previousPriority) return;

      safeUpdate({ priority: value }, () =>
        trackMetadataChange({
          eventName: 'Help Desk - Admin Changed Priority',
          assignedAdminId: ticket?.assignedAdminId || null,
          newValue: value,
          previousValue: previousPriority
        })
      );
    },
    [safeUpdate, ticket?.assignedAdminId, ticket?.priority, trackMetadataChange]
  );

  // Assignee change
  const handleAssigneeChange = useCallback(
    (value) => {
      const assignedAdminId = value ? Number(value) : null;
      const previousAssignedAdminId = ticket?.assignedAdminId || null;
      if (assignedAdminId === previousAssignedAdminId) return;

      safeUpdate({ assignedAdminId }, () =>
        trackMetadataChange({
          eventName: 'Help Desk - Admin Assigned Ticket',
          assignedAdminId,
          newValue: assignedAdminId,
          previousValue: previousAssignedAdminId
        })
      );
    },
    [safeUpdate, ticket?.assignedAdminId, trackMetadataChange]
  );

  return {
    categories,
    admins,
    updateMutation: { mutate: safeUpdate, isLoading: updateLoading },
    isEditable,
    pendingCategory,
    handleCategoryChange,
    confirmCategoryChange,
    cancelCategoryChange,
    handlePriorityChange,
    handleAssigneeChange
  };
};

export default useHelpDeskTicketActions;
